import FileUpload from "components/FileUpload";
import { useTooltip } from "context/TooltipProvider";
import { FC, useEffect, useState } from "react";
import { addVictim, editVictim } from "services/lealIntityService";
import { CrimeReference, Investigation, Victim } from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandlerOnForm } from "utils/helper";

type Props = {
  isOpen: boolean;
  action: "Add" | "Edit";
  investigation: Investigation;
  incident?: CrimeReference;
  victim?: Victim;
  onClose: (refetch: boolean) => void;
};

const defaultVictim: Victim = {
  uu_id: 0,

  investigation: null,
  first_name: "",
  middle_name: "",
  last_name: "",
  gender: "Male",
  age: null,
  education_level: "",

  job_position: "",
  ethnicity: "",
  nationality: "",
  religion: "",
  date_of_birth: new Date(),
  phone_number: "",
  email: "",
  identity_card_number: "",
  region: "",
  zone: "",
  woreda: "",
  kebele: "",
  house_number: "",
  city: "",

  picture_file: "",
  injury_details: "",
  statement: "",
};

const VictimPersonalForm: FC<Props> = ({
  isOpen,
  action,
  investigation,
  incident,
  victim,
  onClose,
}) => {
  const { showTooltip } = useTooltip();
  const [formState, setFormState] = useState<Victim>(victim ?? defaultVictim);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (victim && (investigation || incident)) {
      setFormState(victim);
    }
  }, [victim, investigation, incident]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (!formState.first_name) return "First Name is Required.";
    // else if (!formState.middle_name) return "Middle Name is Required.";
    // else if (!formState.last_name) return "Last Name is Required.";
    // else if (!formState.gender) return "Gender is Required.";
    // else if (!formState.age) return "Age is Required.";

    return "";
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      return;
    }

    const updatedPicture =
      action === "Edit"
        ? typeof formState.picture_file === "object"
          ? formState?.picture_file
          : undefined
        : formState.picture_file;

    setLoading(true);
    const submitAction = action === "Edit" ? editVictim : addVictim;
    submitAction({
      ...formState,
      investigation: investigation ? investigation?.uu_id : null,
      Incident_Registration_number: incident?.Incident_Registration_number,
      crimereference: incident ? (incident?.uu_id as number) : null,
      picture_file: updatedPicture,
    })
      .then(() => {
        showTooltip(
          action === "Edit"
            ? "Successfully Updated!"
            : "Successfully Submitted!",
          "success",
        );
        setFormState(defaultVictim);
        onClose(true);
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleClickOutside = (e: any) => {
    if (e.target.classList.contains("fixed")) {
      onClose(false);
    }
  };

  const handleFile = (file: File | null) => {
    setFormState({ ...formState, picture_file: file });
  };

  if (!isOpen) return null;

  return (
    <div
      style={{ marginTop: "0px" }}
      className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-50 backdrop-blur-sm p-4"
      onClick={handleClickOutside}
    >
      <div className="relative w-full max-w-2xl bg-white p-8 shadow-lg rounded-lg overflow-y-auto">
        <h2 className="mb-1 text-2xl font-semibold">{action} Victim</h2>
        <p className="mb-6 text-xs font-light text-[#9D9D9D]">
          publishing industries as a temporary filler.
        </p>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="grid grid-cols-2 gap-2 gap-x-4 mt-4">
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="first_name"
                value={formState?.first_name}
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Middle Name
              </label>
              <input
                type="text"
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="middle_name"
                value={formState?.middle_name}
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Last Name
              </label>
              <input
                type="text"
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="last_name"
                value={formState?.last_name}
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col mt-4">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Gender
              </label>
              <select
                required
                name="gender"
                value={formState?.gender ? formState?.gender : ""}
                onChange={handleChange}
                className="border border-gray-300 rounded-lg bg-[#F2F2F2] p-2 pr-8 focus:outline-none focus:ring focus:ring-blue-300"
              >
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </div>
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Age
              </label>
              <input
                type="number"
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="age"
                value={formState?.age ? formState?.age : ""}
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Educational Level
              </label>
              <input
                type="phone"
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="education_level"
                value={
                  formState?.education_level ? formState?.education_level : ""
                }
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Job Position
              </label>
              <input
                type="phone"
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="job_position"
                value={formState?.job_position ? formState?.job_position : ""}
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Ethnicity
              </label>
              <input
                type="phone"
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="ethnicity"
                value={formState?.ethnicity ? formState?.ethnicity : ""}
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Nationality
              </label>
              <input
                type="phone"
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="nationality"
                value={formState?.nationality ? formState?.nationality : ""}
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Religion
              </label>
              <input
                type="phone"
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="religion"
                value={formState?.religion ? formState?.religion : ""}
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Birth Date
              </label>
              <input
                type="date"
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="date_of_birth"
                value={
                  formState?.date_of_birth
                    ? new Date(formState.date_of_birth)
                        .toISOString()
                        .split("T")[0]
                    : ""
                }
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Phone Number
              </label>
              <input
                type="phone"
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="phone_number"
                value={formState?.phone_number ? formState?.phone_number : ""}
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Email
              </label>
              <input
                type="email"
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="email"
                value={formState?.email ? formState?.email : ""}
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Identity Card Number
              </label>
              <input
                type="number"
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="identity_card_number"
                value={
                  formState?.identity_card_number
                    ? formState?.identity_card_number
                    : ""
                }
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Region
              </label>
              <input
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="region"
                value={formState?.region ? formState?.region : ""}
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Zone
              </label>
              <input
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="zone"
                value={formState?.zone ? formState?.zone : ""}
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Woreda
              </label>
              <input
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="woreda"
                value={formState?.woreda ? formState?.woreda : ""}
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Kebele
              </label>
              <input
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="kebele"
                value={formState?.kebele ? formState?.kebele : ""}
                onChange={handleChange}
              />
            </div>

            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                House Number
              </label>
              <input
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="house_number"
                value={formState?.house_number ? formState?.house_number : ""}
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                City
              </label>
              <input
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="city"
                value={formState?.city ? formState?.city : ""}
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Injury Details
              </label>
              <input
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="injury_details"
                value={
                  formState?.injury_details ? formState?.injury_details : ""
                }
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="flex flex-col mt-4 w-full">
            <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
              Statement
            </label>
            <textarea
              rows={4}
              className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
              name="statement"
              value={formState?.statement ? formState?.statement : ""}
              onChange={handleChange}
            />
          </div>

          <div className="mt-4">
            <FileUpload
              label="Profile Photo"
              fileTypes="image/*"
              info="Upload Files Images "
              required={false}
              onChange={handleFile}
            />
          </div>

          <div className="flex justify-end mt-4">
            <button
              type="submit"
              className={`border border-[#212121] bg-[#212121] px-[76px] py-2 
                                  text-white font-[Poppins] cursor-pointer font-[500] text-base rounded-md hover:bg-[#414040]
                                          ${
                                            loading
                                              ? "opacity-50 cursor-not-allowed"
                                              : ""
                                          }`}
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  {LoadingIcon}
                  Loading...
                </div>
              ) : (
                action + " Victim"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VictimPersonalForm;
